import { useMutation } from '@tanstack/react-query';

import { useRequest, type MutationFunction } from '@frontend/api';

export type TableExtractionPayload = {
  pageNumber: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  cleanUpTempAnnotation: () => void;
};

type SubmitTableForExtractionRequestBody = TableExtractionPayload & {
  fileUuid: string;
};

/**
 * Mutation function for submitting a table for extraction from a PDF.
 * Sends coordinates and page number to the API for async processing.
 * Returns a 201 Created response to indicate the job has been started.
 */
export const useSubmitTableForExtraction: MutationFunction<
  Record<string, never>,
  SubmitTableForExtractionRequestBody
> = ({ requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useMutation({
    mutationFn: (body) => {
      const { fileUuid, pageNumber, x1, x2, y1, y2 } = body;
      const urlPath = `/parts/files/${fileUuid}/analyze_cropped_table`;

      return request(urlPath, {
        init: {
          method: 'POST',
          body: {
            page_number: pageNumber,
            x1,
            x2,
            y1,
            y2,
          },
        },
      });
    },
    ...queryOptions,
    mutationKey: ['submitTableForExtraction'],
  });
};
